import _async_to_generator from "@swc/helpers/src/_async_to_generator.mjs";
import _ts_generator from "@swc/helpers/src/_ts_generator.mjs";
import { useState, useEffect, useRef } from "react";
import { targetActivities } from "./constants";
/**
 * Custom hook for retrieving Adobe Target activity data.
 *
 * @param activityConst - The constant representing the activity.
 * @param targetTimeout - The timeout duration for retrieving the activity data (default: 3000).
 * @returns The activity data or null if not found.
 * @example
 *  const experimentData = useAdobeTarget('EXAMPLE_ACTIVITY')
 *  const experimentIsActive = activityData?.ActivityState === 'Variant'
 *
 *  if (experimentIsActive) {
 *  // Do something
 *  }
 */ export function useAdobeTarget(activityConst) {
    var targetTimeout = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 3000;
    var activityId = getActivityId(activityConst);
    var ref = useState(function() {
        var ref;
        if ((ref = getTargetPropertyFromWindow()) === null || ref === void 0 ? void 0 : ref.length) {
            return getTargetActivity(activityId);
        }
        return;
    }), activityData = ref[0], setActivityData = ref[1];
    var didCancel = useRef(false);
    var setActivityDataIfMounted = function(value) {
        if (didCancel.current === true) {
            return;
        }
        setActivityData(value);
    };
    useEffect(function() {
        function handle() {
            return _handle.apply(this, arguments);
        }
        function _handle() {
            _handle = _async_to_generator(function() {
                var ref, activityData, error;
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            _state.trys.push([
                                0,
                                2,
                                ,
                                3
                            ]);
                            return [
                                4,
                                timeoutPromise(targetTimeout, windowMonitor())
                            ];
                        case 1:
                            _state.sent();
                            if ((ref = getTargetPropertyFromWindow()) === null || ref === void 0 ? void 0 : ref.length) {
                                activityData = getTargetActivity(activityId);
                                if (!activityData) {
                                    setActivityDataIfMounted(null);
                                    console.warn("\uD83E\uDDEA Experiment ".concat(activityConst, " - ").concat(activityId, " not found"));
                                    return [
                                        2
                                    ];
                                }
                                setActivityDataIfMounted(activityData);
                                return [
                                    2
                                ];
                            }
                            setActivityDataIfMounted(null);
                            return [
                                3,
                                3
                            ];
                        case 2:
                            error = _state.sent();
                            console.warn("\uD83E\uDDEA Error while retrieving experiments - ".concat(error));
                            setActivityDataIfMounted(null);
                            return [
                                3,
                                3
                            ];
                        case 3:
                            return [
                                2
                            ];
                    }
                });
            });
            return _handle.apply(this, arguments);
        }
        handle();
        return function() {
            didCancel.current = true;
        };
    }, [
        activityConst,
        targetTimeout
    ]);
    return activityData;
}
/**
 * Wraps a promise with a timeout, rejecting it if it takes longer than the specified time.
 * @param ms The timeout duration in milliseconds.
 * @param promise The promise to wrap with a timeout.
 * @param error The error to be thrown if the promise times out. Default is 'Timed out'.
 * @returns A promise that resolves with the result of the passed in promise, or rejects with the specified error if it times out.
 */ function timeoutPromise(ms, promise) {
    var error = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : new Error("Timed out");
    // Create a promise that rejects in <ms> milliseconds
    var timeout = new Promise(function(_, reject) {
        setTimeout(function() {
            return reject(error);
        }, ms);
    });
    // Return race between timeout and passed in promise
    return Promise.race([
        promise,
        timeout
    ]);
}
/**
 * Monitors the window for the adobe target property (using getTargetPropertyFromWindow) and resolves the promise when the property is available.
 * @returns A promise that resolves when the property is available.
 */ function windowMonitor() {
    return new Promise(function(resolve) {
        var interval = setInterval(function() {
            var result = getTargetPropertyFromWindow();
            if (result !== undefined) {
                clearInterval(interval);
                resolve();
            }
        }, 100);
    });
}
/**
 * Retrieves the activity ID associated with the given activity constant.
 * @param activityConst The activity constant.
 * @returns The activity ID.
 */ function getActivityId(activityConst) {
    var activityId = targetActivities[activityConst];
    if (typeof activityId === "undefined") {
        // eslint-disable-next-line no-console
        console.warn("Activity ID: ".concat(activityConst, " is missing from targetActivities in packages/hooks/target/src/constants.ts"));
    }
    return activityId;
}
/**
 * Retrieves the Adobe Target property from the window object.
 * @returns An array of TargetResponseToken objects or undefined if the window object is not available.
 */ function getTargetPropertyFromWindow() {
    return "object" === "undefined" ? undefined : window["ttMETA"];
}
/**
 * Retrieves the target activity with the specified activity ID.
 * @param activityId The ID of the target activity.
 * @returns The target activity object if found, or null if not found.
 */ function getTargetActivity(activityId) {
    var ref;
    var targetActivity = (ref = getTargetPropertyFromWindow()) === null || ref === void 0 ? void 0 : ref.find(function(activity) {
        return activity.ActivityId === activityId;
    });
    return targetActivity !== null && targetActivity !== void 0 ? targetActivity : null;
}
export default useAdobeTarget;
